import { union, sortBy, uniq, identity, isEmpty, mapValues, omit, keyBy, isEqual } from 'lodash'

import { hierarchyLevel } from '../store/initialState'
import { patchFile, load as loadUI } from './ui'
import {
  SELECT_FILE,
  SELECT_EMPTY_FILE,
  SHOW_LOADER,
  UNSET_FILE_LOADED,
  SET_RESUMING,
  SET_CHECKING_FOR_OFFLINE_DRIFT,
  SET_OVERWRITING_CLOUD_WITH_BACKUP,
  SET_SHOW_RESUME_MESSAGE_DIALOG,
  SET_BACKING_UP_OFFLINE_FILE,
  START_CREATING_NEW_PROJECT,
  FINISH_CREATING_NEW_PROJECT,
  ADD_BOOK_FROM_PLTR,
  CLOSE_IMPORT_PLOTTR_MODAL,
  ADD_CHARACTER_FROM_PLTR,
  ADD_PLACE_FROM_PLTR,
  ADD_NOTE_FROM_PLTR,
  ADD_TAG_FROM_PLTR,
  ADD_IMAGE_FROM_PLTR,
  SET_CONFLICT_RESOLUTION_DATA,
  SET_CONFLICT_RESOLUTION_ITEM_ACTION,
  ACTION_CONFLICT_CHANGES,
  BOOTED_OFFLINE_FILE_WHILST_ONLINE,
  CLOSE_CONFLICT_RESOLUTION_MODAL,
} from '../constants/ActionTypes'
import selectors from '../selectors'
import { addCardAttr, addNoteAttr, addPlaceAttr } from './customAttributes'
import { imageId, nextId } from '../store/newIds'
import { adjustIds, newTree } from '../reducers/tree'
import { removeExistingAttributes } from '../helpers/customAttributes'
import { nextId as nextBeatId } from '../helpers/nextBeatId'

/**
 * @template A
 * @typedef {import('../types/plottr').Importable<A>} Importable
 */

/**
 * @typedef {import('../types/plottr').ImportModalUiData} ImportModalUiData
 * @typedef {import('../types/plottr').FamilyTree} FamilyTree
 * @typedef {import('../types/plottr').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('../types/system').FilePermission} FilePermission
 * @typedef {import('../types/system').HierarchyLevels} HierarchyLevelsSingleBook
 * @typedef {import('../types/system').ConflictChanges} ConflictChanges
 * @typedef {import('../types/system').LocalAndOnlineConflictChanges} LocalAndOnlineConflictChanges
 * @typedef {import('../types/system').BookWithAllAssociatedData} BookWithAllAssociatedData
 * @typedef {import('../types/system').ConflictResolutionLocalOnly} ConflictResolutionLocalOnly
 * @typedef {import('../types/system').ConflictResolutionChangedLocalAndOnline} ConflictResolutionChangedLocalAndOnline
 * @typedef {import('../types/system').SeriesConflict} SeriesConflict
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').FileURL} FileURL
 * @typedef {import('../index').ProjectTemplate} ProjectTemplate
 * @typedef {import('../index').PlotlineTemplate} PlotlineTemplate
 * @typedef {import('../index').PlotlineCustomTemplate} PlotlineCustomTemplate
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Tag} Tag
 * @typedef {import('../index').PlottrFileBeatsEntry} Beats
 * @typedef {import('../index').Attribute} Attribute
 * @typedef {import('../index').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('../index').FileCategories} FileCategories
 * @typedef {import('../index').Category} Category
 * @typedef {import('../index').Series} Series
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').ImageRecord} ImageRecord
 * @typedef {import('../index').Logger} Logger
 * @typedef {import('../types/plottr').HierarchyLevels} HierarchyLevels
 */

/**
 * @typedef {ImageRecord & { newId: number }} ImageRecordWithNewId
 */

/**
 * @typedef {Array<Category>} Categories
 */

/**
 * @template A
 * @typedef {import('../types/system').ActionableData<A>} ActionableData
 */

/**
 * @template A
 * @typedef {import('../types/system').LocalAndOnline<A>} LocalAndOnline
 */

/**
 * @typedef {{ type: 'ADD_BOOK_FROM_PLTR', book: Book, id: 'series' | number, cards: Array<Card>, lines: Array<Line>, beats: Beats, hierarchyLevels: HierarchyLevels, images: Record<String, ImageRecordWithNewId>, newCards: Array<Card>, newBeats: Beats, newLines: Array<Line>, nextCardId: number, nextLineId: number, nextBeatId: number, newBookId: number, imageId: number | Null }} AddBookFromPLTRAction
 */

/**
 * @typedef {ReturnType<typeof selectFile> | ReturnType<typeof selectEmptyFile> | ReturnType<typeof _addImageFromPltr> | ReturnType<typeof _addCharacterFromPltr> | ReturnType<typeof _addPlaceFromPltr> | ReturnType<typeof _addTagFromPltr> | ReturnType<typeof _addNoteFromPltr> | ReturnType<typeof _closeImportModal> | ReturnType<typeof _setConflictResolutionData> | ReturnType<typeof _closeConflictResolutionModal> | ReturnType<typeof setConflictResolutionAction> | ReturnType<typeof _actionConflictChanges> | ReturnType<typeof bootedOfflineFileWhilstOnline> | ReturnType<typeof setResuming> | ReturnType<typeof setOverwritingCloudWithBackup> | ReturnType<typeof startCreatingNewProject> | ReturnType<typeof showLoader> | ReturnType<typeof unsetFileLoaded> | ReturnType<typeof setCheckingForOfflineDrift> | ReturnType<typeof setShowResumeMessageDialog> | ReturnType<typeof setBackingUpOfflineFile> | ReturnType<typeof finishCreatingNewProject> | AddBookFromPLTRAction} ProjectActions
 */

const {
  importModalBookDataSelector,
  importPltrDataSelector,
  imagesSelector,
  allCharactersSelector,
  allPlacesSelector,
  allTagsSelector,
  allBooksWithAllAssociatedDataSelector,
  allNotesSelector,
  singleTagSelector,
  singleCharacterSelector,
  singlePlaceSelector,
  singleBookSelector,
  localOnlyConflictChangesSelector,
  localAndOnlineConflictChangesSelector,
  seriesConflictSelector,
  onlineFileForConflictResolutionSelector,
  attributesSelector,
  allCustomAttributesSelector,
  conflictResolutionCustomAttributesSelector,
  characterAttributesForBookSelector,
  allCategoriesSelector,
  allBeatsSelector,
  nextLineIdSelector,
  nextCardIdSelector,
  allBooksAsArraySelector,
  seriesSelector,
  fullFileStateSelector,
  permissionSelector,
  cardsCustomAttributesSelector,
  placeCustomAttributesSelector,
  noteCustomAttributesSelector,
  fileIdSelector,
  clientIdSelector,
  allFamilyTreesSelector,
} = selectors(identity)

/**
 * @param {function(PlottrState): Void} cb
 * @return {function(Dispatch, GetState): Void}
 */
export const withFullFileState = (cb) => (dispatch, getState) => {
  cb(getState())
}

/**
 * IMPORTANT: This action must be called prior to loading a file so
 * that the permission for that file is set, and the file is set to
 * unloaded prior to actually loading the file.  This creates a brief
 * moment where no subsequent synchronisations should happen (if the
 * fileURL changed for some reason) prior to loading the new file.
 *
 * @param {FilePermission} permission
 * @param {FileURL} fileURL
 * @return {{ type: 'SELECT_FILE', permission: FilePermission, fileURL: FileURL }}
 */
export const selectFile = (permission, fileURL) => ({
  type: SELECT_FILE,
  permission,
  fileURL,
})

/**
 * @return {{ type: 'SELECT_EMPTY_FILE' }}
 */
export const selectEmptyFile = () => ({
  type: SELECT_EMPTY_FILE,
})

/**
 * @return {{ type: UNSET_FILE_LOADED }}
 */
export const unsetFileLoaded = () => ({
  type: UNSET_FILE_LOADED,
})

/**
 * @param {boolean} isLoading
 * @return {{ type: SHOW_LOADER, isLoading: boolean }}
 */
export const showLoader = (isLoading) => ({
  type: SHOW_LOADER,
  isLoading,
})

/**
 * @param {boolean} resuming
 * @return {{ type: SET_RESUMING, resuming: boolean }}
 */
export const setResuming = (resuming) => ({
  type: SET_RESUMING,
  resuming,
})

/**
 * @param {boolean} checkingOfflineDrift
 * @return {{ type: SET_CHECKING_FOR_OFFLINE_DRIFT, checkingOfflineDrift: boolean }}
 */
export const setCheckingForOfflineDrift = (checkingOfflineDrift) => ({
  type: SET_CHECKING_FOR_OFFLINE_DRIFT,
  checkingOfflineDrift,
})

/**
 * @param {boolean} overwritingCloudWithBackup
 * @return {{ type: SET_OVERWRITING_CLOUD_WITH_BACKUP, overwritingCloudWithBackup: boolean }}
 */
export const setOverwritingCloudWithBackup = (overwritingCloudWithBackup) => ({
  type: SET_OVERWRITING_CLOUD_WITH_BACKUP,
  overwritingCloudWithBackup,
})

/**
 * @param {boolean} showResumeMessageDialog
 * @return {{ type: SET_SHOW_RESUME_MESSAGE_DIALOG, showResumeMessageDialog: boolean }}
 */
export const setShowResumeMessageDialog = (showResumeMessageDialog) => ({
  type: SET_SHOW_RESUME_MESSAGE_DIALOG,
  showResumeMessageDialog,
})

/**
 * @param {boolean} backingUpOfflineFile
 * @return {{ type: SET_BACKING_UP_OFFLINE_FILE, backingUpOfflineFile: boolean }}
 */
export const setBackingUpOfflineFile = (backingUpOfflineFile) => ({
  type: SET_BACKING_UP_OFFLINE_FILE,
  backingUpOfflineFile,
})

/**
 * @param {ProjectTemplate | PlotlineTemplate | PlotlineCustomTemplate | Null} template
 * @param {String | Null} defaultName
 * @return {{ type: START_CREATING_NEW_PROJECT, template: ProjectTemplate | PlotlineTemplate | PlotlineCustomTemplate | Null, defaultName: String | Null }}
 */
export const startCreatingNewProject = (template, defaultName) => ({
  type: START_CREATING_NEW_PROJECT,
  template,
  defaultName,
})

/**
 * @return {{ type: FINISH_CREATING_NEW_PROJECT }}
 */
export const finishCreatingNewProject = () => ({
  type: FINISH_CREATING_NEW_PROJECT,
})

/**
 * @param {ImageRecord} image
 * @param {number} newId
 * @return {{ type: 'ADD_IMAGE_FROM_PLTR', image: ImageRecord, newId: number }}
 */
export const _addImageFromPltr = (image, newId) => {
  return {
    type: ADD_IMAGE_FROM_PLTR,
    image,
    newId,
  }
}

/**
 * @param {Character} character
 * @param {number} newId
 * @return {{ type: 'ADD_CHARACTER_FROM_PLTR', character: Character, newId: number }}
 */
export const _addCharacterFromPltr = (character, newId) => {
  return {
    type: ADD_CHARACTER_FROM_PLTR,
    character,
    newId,
  }
}

/**
 * @param {Place} place
 * @param {number} newId
 * @return {{ type: 'ADD_PLACE_FROM_PLTR', place: Place, newId: number }}
 */
export const _addPlaceFromPltr = (place, newId) => {
  return {
    type: ADD_PLACE_FROM_PLTR,
    place,
    newId,
  }
}

/**
 * @param {Tag} tag
 * @param {number} newId
 * @return {{ type: 'ADD_TAG_FROM_PLTR', tag: Tag, newId: number }}
 */
export const _addTagFromPltr = (tag, newId) => {
  return {
    type: ADD_TAG_FROM_PLTR,
    tag,
    newId,
  }
}

/**
 * @param {Note} note
 * @param {number} newId
 * @return {{ type: 'ADD_NOTE_FROM_PLTR', note: Note, newId: number }}
 */
export const _addNoteFromPltr = (note, newId) => {
  return {
    type: ADD_NOTE_FROM_PLTR,
    note,
    newId,
  }
}

/**
 * @param {Book} book
 * @param {'series' | number} id
 * @param {Array<Card>} cards
 * @param {Array<Line>} lines
 * @param {Beats} beats
 * @param {HierarchyLevels} hierarchyLevels
 * @param {Record<String, ImageRecordWithNewId>} images
 * @return {{ type: 'ADD_BOOK_FROM_PLTR', book: Book, id: 'series' | number, cards: Array<Card>, lines: Array<Line>, beats: Beats, hierarchyLevels: HierarchyLevels, images: Record<String, ImageRecordWithNewId> }}
 */
export const _addBookFromPltr = (book, id, cards, lines, beats, hierarchyLevels, images) => {
  return {
    type: ADD_BOOK_FROM_PLTR,
    book,
    id,
    cards,
    lines,
    beats,
    hierarchyLevels,
    images,
  }
}

/**
 * @return {{ type: 'CLOSE_IMPORT_PLOTTR_MODAL' }}
 */
export const _closeImportModal = () => {
  return {
    type: CLOSE_IMPORT_PLOTTR_MODAL,
  }
}

/**
 * @param {String} x
 * @return {number | Null}
 */
const parseIntStrictly = (x) => {
  try {
    const result = parseInt(x)
    if (String(result) === x) {
      return result
    } else {
      return null
    }
  } catch (_error) {
    return null
  }
}

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const saveImportPltrData = () => (dispatch, getState) => {
  const state = getState()
  const importData = importPltrDataSelector(state)
  const bookData = importModalBookDataSelector(state)
  if (importData && bookData) {
    const currentCharacters = allCharactersSelector(state)
    const currentPlaces = allPlacesSelector(state)
    const currentTags = allTagsSelector(state)
    const currentNotes = allNotesSelector(state)

    const books = importData.books
    const hierarchyLevels = bookData.hierarchyLevels
    const beats = bookData.beats
    const cards = bookData.cards
    const lines = bookData.lines
    const customAttributes = importData.customAttributes
    const notes = importData.notes
    const characters = importData.characters
    const places = importData.places
    const tags = importData.tags
    const images = importData.images

    const cardCustomAttr = cardsCustomAttributesSelector(state)
    const placesCustomAttr = placeCustomAttributesSelector(state)
    const notesCustomAttr = noteCustomAttributesSelector(state)

    const allImages = imagesSelector(state)
    const newImageId = imageId(allImages)
    const newCharacterId = nextId(currentCharacters)
    const newPlaceId = nextId(currentPlaces)
    const newTagId = nextId(currentTags)
    const newNoteId = nextId(currentNotes)

    /**
     * @type {ImportModalUiData['images']}
     */
    const initialImageRecords = {}

    /**
     * NOTE(ed): this indexing operation has the side-effect of also
     * adding the images whilst it indexes.
     *
     * @type {ImportModalUiData['images']}
     */
    const newImportableImagesIndexedBySourceIds = Object.values(images)
      .map((importableImage, idx) => {
        const newId = newImageId + idx
        const hasBookImageToSave = [...Object.values(omit(books, 'allIds'))].some(
          (i) => i.isChecked && String(i.data.imageId) == String(importableImage.data.id)
        )
        const hasCharacterImageToSave = characters.some(
          (i) => i.isChecked && String(i.data.imageId) == String(importableImage.data.id)
        )
        const hasNoteImageToSave = notes.some(
          (i) => i.isChecked && String(i.data.imageId) == String(importableImage.data.id)
        )
        const hasPlaceImageToSave = places.some(
          (i) => i.isChecked && String(i.data.imageId) == String(importableImage.data.id)
        )
        if (
          hasBookImageToSave ||
          hasCharacterImageToSave ||
          hasNoteImageToSave ||
          hasPlaceImageToSave
        ) {
          dispatch(_addImageFromPltr(importableImage.data, newId))
          return {
            ...importableImage,
            newId,
          }
        } else {
          return null
        }
      })
      .reduce((acc, item) => {
        if (item) {
          acc[item.data.id] = item
        }
        return acc
      }, initialImageRecords)

    /**
     * @typedef {ImportModalUiData['characters'][0]} CharacterImportItem
     */

    /**
     * @type {Record<number, CharacterImportItem>}
     */
    const initialImportableCharacters = {}

    /**
     * NOTE(ed): this indexing operation has the side-effect of also
     * adding the Characters whilst it indexes.
     *
     * @type {Record<number, CharacterImportItem>}
     */
    const newImportableCharactersIndexedBySourceIds = characters
      .map((importableCharacter, idx) => {
        if (importableCharacter.isChecked) {
          /**
           * @type {number}
           */
          const newId = newCharacterId + idx
          /**
           * @type {CharacterImportItem}
           */
          const newImportableCharacter = {
            ...importableCharacter,
            newId,
            data: {
              ...importableCharacter.data,
              bookIds: [],
              tags: [],
              attributes: [],
              noteIds: [],
              cards: [],
              templates: [],
              categoryId: null,
              imageId:
                importableCharacter.data.imageId &&
                newImportableImagesIndexedBySourceIds[importableCharacter.data.imageId]?.newId
                  ? String(
                      newImportableImagesIndexedBySourceIds[importableCharacter.data.imageId]?.newId
                    )
                  : null,
            },
          }
          dispatch(_addCharacterFromPltr(newImportableCharacter.data, newId))

          return {
            ...newImportableCharacter,
            newId,
          }
        } else {
          return null
        }
      })
      .reduce((acc, obj) => {
        if (obj) {
          acc[obj.data.id] = obj
        }
        return acc
      }, initialImportableCharacters)

    /**
     * @typedef {ImportModalUiData['places'][0]} PlaceImportItem
     */

    /**
     * @type {Record<number, PlaceImportItem>}
     */
    const initialPlaces = {}

    /**
     * NOTE(ed): this indexing operation has the side-effect of also
     * adding the places whilst it indexes.
     *
     * @type {Record<number, PlaceImportItem>}
     */
    const newImportablePlacesIndexedBySourceIds = places
      .map((place, idx) => {
        if (place.isChecked) {
          const newId = newPlaceId + idx
          /**
           * @type {PlaceImportItem}
           */
          const newPlace = {
            ...place,
            newId,
            data: {
              ...place.data,
              bookIds: [],
              tags: [],
              noteIds: [],
              templates: [],
              cards: [],
              categoryId: null,
              imageId:
                place.data.imageId &&
                newImportableImagesIndexedBySourceIds[place.data.imageId]?.newId
                  ? String(newImportableImagesIndexedBySourceIds[place.data.imageId]?.newId)
                  : null,
            },
          }
          dispatch(_addPlaceFromPltr(newPlace.data, newId))

          return {
            ...newPlace,
            newId,
          }
        } else {
          return null
        }
      })
      .reduce((acc, obj) => {
        if (obj) {
          acc[obj.data.id] = obj
        }
        return acc
      }, initialPlaces)

    /**
     * @typedef {ImportModalUiData['tags'][0]} TagImportItem
     */

    /**
     * @type {Record<number, TagImportItem>}
     */
    const initialTags = {}

    /**
     * NOTE(ed): this indexing operation has the side-effect of also
     * adding the tags whilst it indexes.
     *
     * @type {Record<number, TagImportItem>}
     */
    const newImportableTagsIndexedBySourceIds = tags
      .map((tag, idx) => {
        if (tag.isChecked) {
          const newId = newTagId + idx
          const newTag = {
            ...tag,
            newId,
            data: {
              ...tag.data,
              categoryId: null,
            },
          }
          dispatch(_addTagFromPltr(newTag.data, newId))

          return {
            ...newTag,
            newId,
          }
        } else {
          return null
        }
      })
      .reduce((acc, obj) => {
        if (obj) {
          acc[obj.data.id] = obj
        }
        return acc
      }, initialTags)

    /**
     * @typedef {ImportModalUiData['notes'][0]} NoteImportItem
     */

    /**
     * @type {Record<number, NoteImportItem>}
     */
    const initialNotes = {}

    /**
     * NOTE(ed): this indexing operation has the side-effect of also
     * adding the Notes whilst it indexes.
     *
     * @type {Record<number, NoteImportItem>}
     */
    const newImportableNotesIndexedBySourceIds = notes
      .map((note, idx) => {
        if (note.isChecked) {
          const newId = newNoteId + idx
          /**
           * @type {NoteImportItem}
           */
          const newNote = {
            ...note,
            newId,
            data: {
              ...note.data,
              tags: [],
              characters: [],
              templates: [],
              bookIds: [],
              places: [],
              categoryId: null,
              imageId:
                note.data.imageId && newImportableImagesIndexedBySourceIds[note.data.imageId]?.newId
                  ? String(newImportableImagesIndexedBySourceIds[note.data.imageId].newId)
                  : null,
            },
          }
          dispatch(_addNoteFromPltr(newNote.data, newId))
          return newNote
        } else {
          return null
        }
      })
      .reduce((acc, obj) => {
        if (obj) {
          acc[obj.data.id] = obj
        }
        return acc
      }, initialNotes)

    /**
     * @param {Array<number>} acc
     * @param {number | null} next
     * @return {Array<number>}
     */
    const noNulls = (acc, next) => {
      if (next) {
        return [...acc, next]
      } else {
        return acc
      }
    }

    mapValues(books, (bookItem, rawBookId) => {
      const bookId = rawBookId === 'series' ? 'series' : parseIntStrictly(rawBookId)
      if (bookId && bookItem.isChecked) {
        const newCards = cards.map((card) => {
          /**
           * @param {Record<number, Importable<Character | Place | Tag | Note>>} index
           * @return {function(number): (number | Null)}
           */
          const mapId = (index) => (id) => {
            const newId = index[id]?.newId
            if (newId && String(id) === String(index[id]?.data?.id)) {
              return newId
            } else {
              return null
            }
          }

          // NOTE(ed): the following replaces old character, place and
          // tag ids inside the cards.
          /**
           * @type {Array<number>}
           */
          const newCharacterIds = (card.characters || [])
            .map(mapId(newImportableCharactersIndexedBySourceIds))
            .reduce(noNulls, [])
          /**
           * @type {Array<number>}
           */
          const newPlaceIds = (card.places || [])
            .map(mapId(newImportablePlacesIndexedBySourceIds))
            .reduce(noNulls, [])
          /**
           * @type {Array<number>}
           */
          const newTagIds = (card.tags || [])
            .map(mapId(newImportableTagsIndexedBySourceIds))
            .reduce(noNulls, [])
          /**
           * @type {Array<number>}
           */
          const newNoteIds = (card.notes || [])
            .map(mapId(newImportableNotesIndexedBySourceIds))
            .reduce(noNulls, [])

          return {
            ...card,
            fromTemplateId: null,
            templates: [],
            characters: newCharacterIds,
            places: newPlaceIds,
            tags: newTagIds,
            notes: newNoteIds,
          }
        })
        // TODO(ed): I'm not a fan of computing some of the ids here
        // and then some in the root reducer.  We should figure out
        // everything in one place.  This is probably the right place.
        //
        // Today, lines, beats, books, cards and hierarchy all get new
        // ids via the root reducer. :/
        const newLines = lines.filter((l) => l.id && String(l.bookId) === String(bookId))
        const newBeats = !isEmpty(beats[bookId]) ? beats[bookId] : newTree('id')
        const newHierarchyLevels =
          !isEmpty(hierarchyLevels) && !isEmpty(hierarchyLevels[bookId])
            ? hierarchyLevels[bookId]
            : { 0: hierarchyLevel() }
        const newImages = Object.values(newImportableImagesIndexedBySourceIds).reduce(
          (acc, next) => {
            if (next.newId) {
              return {
                ...acc,
                [next.newId]: {
                  ...next.data,
                  id: next.newId,
                },
              }
            } else {
              return acc
            }
          },
          {}
        )
        dispatch(
          // NOTE(ed): We add extra values to the action in the root
          // reducer.
          //
          // @ts-ignore
          _addBookFromPltr(
            bookItem.data,
            bookId,
            newCards,
            newLines,
            newBeats,
            newHierarchyLevels,
            newImages
          )
        )
      }
    })

    Object.entries(customAttributes).forEach(([sectionName, importableAttributeObject]) => {
      /**
       * @type {Array<LegacyCustomAttribute>}
       */
      const attributeObject = Object.values(importableAttributeObject).reduce((acc, value) => {
        if (value.isChecked) {
          return [...acc, value.data]
        } else {
          return acc
        }
      }, [])
      switch (sectionName) {
        case 'scenes': {
          const newAttrs = removeExistingAttributes(attributeObject, cardCustomAttr)
          newAttrs.forEach((attribute) => {
            addCardAttr(attribute)(dispatch, getState)
          })
          break
        }

        case 'places': {
          const newAttrs = removeExistingAttributes(attributeObject, placesCustomAttr)
          newAttrs.forEach((attribute) => {
            addPlaceAttr(attribute)(dispatch, getState)
          })
          break
        }

        case 'notes': {
          const newAttrs = removeExistingAttributes(attributeObject, notesCustomAttr)
          newAttrs.forEach((attribute) => {
            addNoteAttr(attribute)(dispatch, getState)
          })
          break
        }
      }
    })

    dispatch(_closeImportModal())
  } else {
    throw new Error('Could not find importData')
  }
}

/**
 * @param {{ id: any }} x
 * @return {function({ id: any }): boolean}
 */
const idsAreEqual = (x) => (y) => {
  return x.id === y.id
}

const ATTRIBUTES_TO_STRIP_DURING_DIFF = ['clientId', 'fileURL', 'fileId', 'isCloudFile']

/**
 * @param {BookWithAllAssociatedData} bookBundle
 * @return {BookWithAllAssociatedData}
 */
const stripNonDiffableDataFromBookBundle = (bookBundle) => {
  /**
   * @param {any} x
   * @return {any}
   */
  const omitNonDiffableData = (x) =>
    typeof x === 'object' ? omit(x, ATTRIBUTES_TO_STRIP_DURING_DIFF) : x
  return {
    book: bookBundle.book,
    lines: bookBundle.lines.map(omitNonDiffableData),
    cards: bookBundle.cards.map(omitNonDiffableData),
    beats: omitNonDiffableData(bookBundle.beats),
    hierarchy: omitNonDiffableData(bookBundle.hierarchy),
  }
}

/**
 * @param {Array<BookWithAllAssociatedData>} offlineBooks
 * @param {Array<BookWithAllAssociatedData>} onlineBooks
 * @param {PlottrState} onlineFile
 * @return {[Array<BookWithAllAssociatedData>, Array<LocalAndOnline<BookWithAllAssociatedData>>]}
 */
export const bookDiff = (offlineBooks, onlineBooks, onlineFile) => {
  const startingBeatId = nextBeatId(allBeatsSelector(onlineFile))
  const startingLineId = nextLineIdSelector(onlineFile)
  const startingCardId = nextCardIdSelector(onlineFile)

  // NOTE: this state outlives the `bumpBookKeys` function because we
  // need to adjust the ids of books that are local-only and those
  // that were changed both online and offline.
  /**
   * @type {number}
   */
  let currentBeatId = startingBeatId
  // Old id -> new
  /**
   * @type {Record<number, number>}
   */
  const beatIdMap = {}
  /**
   * @type {number}
   */
  let currentLineId = startingLineId
  // Old id -> new
  /**
   * @type {Record<number, number>}
   */
  const lineIdMap = {}
  /**
   * @type {number}
   */
  let currentCardId = startingCardId
  // Old id -> new
  /**
   * @type {Record<number, number>}
   */
  const cardIdMap = {}
  /**
   * @param {BookWithAllAssociatedData} book
   * @return {BookWithAllAssociatedData}
   */
  const bumpBookKeys = (book) => {
    /**
     * @typedef HasId
     * @property {number} id
     */

    /**
     * @template T
     * @param {Array<Exclude<T, 'id'> & HasId>} entities
     * @param {function(): number} getNextId
     * @param {Record<number, number>} idMap
     * @return {Array<Exclude<T, 'id'> & HasId>}
     */
    const bumpArrayIds = (entities, getNextId, idMap) => {
      return entities.map((entity) => {
        const newId = getNextId()
        idMap[entity.id] = newId
        return {
          ...entity,
          id: newId,
        }
      })
    }

    /**
     * @param {Partial<Beats>} beats
     * @return {Partial<Beats>}
     */
    const bumpBeatIds = (beats) => {
      /**
       * @param {number} id
       * @return {number | Null}
       */
      const adjustId = (id) => {
        if (beatIdMap[id]) {
          return beatIdMap[id]
        } else if (
          id === null ||
          // NOTE(ed): I can't tell why I added this check.  It
          // must've come up when debugging :/
          //
          // @ts-ignore
          id === 'null'
        ) {
          return null
        } else {
          const newId = currentBeatId++
          beatIdMap[id] = newId
          return newId
        }
      }

      return adjustIds('id')(beats, adjustId)
    }

    const newBeats = bumpBeatIds(book.beats)
    const newLines = bumpArrayIds(
      book.lines,
      () => {
        return currentLineId++
      },
      lineIdMap
    )
    const newCards = bumpArrayIds(
      book.cards,
      () => {
        return currentCardId++
      },
      cardIdMap
    ).map((card) => {
      return {
        ...card,
        lineId: card.lineId ? lineIdMap[card.lineId] : card.lineId,
        beatId: card.beatId ? beatIdMap[card.beatId] : card.beatId,
      }
    })
    return {
      ...book,
      lines: newLines,
      cards: newCards,
      beats: newBeats,
    }
  }

  const localOnly = offlineBooks
    .filter(({ book }) => {
      return !onlineBooks.some((onlineBook) => {
        return onlineBook.book.id === book.id
      })
    })
    .map(bumpBookKeys)

  const onlineAndOffline = onlineBooks.filter((onlineBook) => {
    const onlineWithoutNonDiffData = stripNonDiffableDataFromBookBundle(onlineBook)
    const offlineBook = offlineBooks.find(({ book }) => {
      return book.id === onlineBook.book.id
    })
    return (
      offlineBook &&
      !isEqualTreatingUndefinedOrMissingAsTheSame(
        stripNonDiffableDataFromBookBundle(offlineBook),
        onlineWithoutNonDiffData
      )
    )
  })

  return [
    localOnly,
    onlineAndOffline.map((onlineBook) => {
      const offlineBook = offlineBooks.find((book) => {
        return book.book.id === onlineBook.book.id
      })
      return {
        onlineEntity: onlineBook,
        offlineEntity: (offlineBook && bumpBookKeys(offlineBook)) || undefined,
      }
    }),
  ]
}

/**
 * @param {any} one
 * @param {any} other
 * @return {boolean}
 */
export const isEqualTreatingUndefinedOrMissingAsTheSame = (one, other) => {
  if (typeof one !== typeof other) {
    return false
  } else if (Array.isArray(one) && Array.isArray(other)) {
    return (
      one.length === other.length &&
      one.every((x, index) => {
        return isEqualTreatingUndefinedOrMissingAsTheSame(x, other[index])
      })
    )
  } else if (one === null) {
    return other === null
  } else if (typeof one === 'undefined') {
    return typeof other === 'undefined'
  } else if (typeof one === 'object') {
    return (
      Object.entries(one).reduce((acc, next) => {
        const [key, value] = next
        return (
          acc &&
          ((typeof value === 'undefined' && typeof other?.[key] === 'undefined') ||
            isEqualTreatingUndefinedOrMissingAsTheSame(value, other?.[key]))
        )
      }, true) &&
      Object.entries(other).reduce((acc, next) => {
        const [key, value] = next
        return (
          acc &&
          ((typeof value === 'undefined' && typeof one?.[key] === 'undefined') ||
            isEqualTreatingUndefinedOrMissingAsTheSame(value, one?.[key]))
        )
      }, true)
    )
  } else {
    return one === other
  }
}

/**
 * @template A
 * @typedef {[Array<A>, Array<LocalAndOnline<A>>]} EntityDiffs
 */

/**
 * @typedef HasId
 * @property {any} id
 */

/**
 * Produce a two-tuple of the entities which are only on the local
 * version of the file and those which are both online and offline but
 * differ between the two.
 *
 * @template A
 * @param {Array<A & HasId>} offlineEntities
 * @param {Array<A & HasId>} onlineEntities
 * @return {EntityDiffs<A & HasId>}
 */
export const arrayEntityDiff = (offlineEntities, onlineEntities) => {
  const localOnly = offlineEntities.filter((offlineEntity) => {
    return !onlineEntities.some(idsAreEqual(offlineEntity))
  })
  const onlineAndOffline = onlineEntities.filter((onlineEntity) => {
    const onlineWithoutNonDiffData = omit(
      // TS compiler and lodash sometimes don't play together very well.
      // @ts-ignore
      onlineEntity,
      ATTRIBUTES_TO_STRIP_DURING_DIFF
    )
    const offlineEntity = offlineEntities.find(idsAreEqual(onlineEntity))
    return (
      offlineEntity &&
      !isEqualTreatingUndefinedOrMissingAsTheSame(
        omit(
          // TS compiler and lodash sometimes don't play together very well.
          // @ts-ignore
          offlineEntity,
          ATTRIBUTES_TO_STRIP_DURING_DIFF
        ),
        onlineWithoutNonDiffData
      )
    )
  })
  return [
    localOnly,
    onlineAndOffline.map((onlineEntity) => {
      return {
        onlineEntity: onlineEntity,
        offlineEntity: offlineEntities.find(idsAreEqual(onlineEntity)),
      }
    }),
  ]
}

/**
 * @template A
 * @param {A} data
 * @return {ActionableData<A>}
 */
export const defaultLocalAction = (data) => {
  return {
    data,
    action: 'INCLUDE',
  }
}

/**
 * @template A
 * @param {A} data
 * @return {ActionableData<A>}
 */
export const defaultLocalAndOnlineAction = (data) => {
  return {
    data,
    action: 'DUPLICATE',
  }
}

/**
 * @typedef MergedAttributes
 * @property {Record<number, number>} conflictedAttributeMapping
 * @property {Array<Attribute>} mergedAttributes
 */

/**
 * @param {Array<Attribute>} onlineAttributes
 * @param {Array<Attribute>} offlineAttributes
 * @return {MergedAttributes}
 */
export const mergeAttributeType = (onlineAttributes, offlineAttributes) => {
  let maxId = onlineAttributes.reduce((acc, { id }) => {
    if (typeof id === 'number') {
      return Math.max(acc, id)
    } else {
      return id
    }
  }, 0)
  // Offline entities might have their attribute ids shifted by the
  // merge.
  /**
   * @type {Record<number, number>}
   */
  const conflictedAttributeMapping = {}
  /**
   * @type {Record<number, Attribute>}
   */
  const initialMergedAttributes = {}
  const mergedAttributes = Object.values(
    onlineAttributes.concat(offlineAttributes).reduce((acc, next) => {
      const conflict = () => {
        maxId++
        conflictedAttributeMapping[next.id] = maxId
        return {
          ...acc,
          [maxId]: {
            ...next,
            id: maxId,
          },
        }
      }
      const existingAttribute = acc[next.id]
      if (typeof existingAttribute === 'object') {
        if (existingAttribute.name === next.name) {
          if (
            (existingAttribute.type === 'base-attribute' || next.type === 'base-attribute') &&
            existingAttribute.type !== next.type
          ) {
            return conflict()
          } else if (existingAttribute.type !== next.type && next.type === 'paragraph') {
            return {
              ...acc,
              [next.id]: next,
            }
          } else {
            return acc
          }
        } else {
          return conflict()
        }
      } else {
        return {
          ...acc,
          [next.id]: next,
        }
      }
    }, initialMergedAttributes)
  )
  return {
    conflictedAttributeMapping,
    mergedAttributes,
  }
}

/**
 * @param {Record<String, Array<Attribute>>} allOnlineAttributes
 * @param {Record<String, Array<Attribute>>} allOfflineAttributes
 * @return {AllMergedAttributes}
 */
export const mergeAttributes = (allOnlineAttributes, allOfflineAttributes) => {
  return Object.keys(allOnlineAttributes).reduce(
    (acc, name) => {
      return {
        ...acc,
        [name]: mergeAttributeType(allOnlineAttributes[name], allOfflineAttributes[name]),
      }
    },
    {
      characters: {
        conflictedAttributeMapping: {},
        mergedAttributes: [],
      },
    }
  )
}

/**
 * @param {Array<LegacyCustomAttribute>} localCustomAttributes
 * @param {Array<LegacyCustomAttribute>} remoteCustomAttirbutes
 * @return {Array<LegacyCustomAttribute>}
 */
export const mergeCustomAttributeType = (localCustomAttributes, remoteCustomAttirbutes) => {
  /**
   * @type {Record<String, LegacyCustomAttribute>}
   */
  const initialMergedAttributes = {}
  return Object.values(
    localCustomAttributes.concat(remoteCustomAttirbutes).reduce((acc, next) => {
      const { name, type } = next
      const existingAttribute = acc[name]
      if (typeof existingAttribute === 'object') {
        if (type === 'paragraph') {
          return {
            ...acc,
            [name]: next,
          }
        } else {
          return acc
        }
      } else {
        return {
          ...acc,
          [name]: next,
        }
      }
    }, initialMergedAttributes)
  )
}

/**
 * @param {Record<String, Array<LegacyCustomAttribute>>} allLocalCustomAttributes
 * @param {Record<String, Array<LegacyCustomAttribute>>} allRemoteCustomAttirbutes
 * @return {Record<String, Array<LegacyCustomAttribute>>}
 */
export const mergeCustomAttributes = (allLocalCustomAttributes, allRemoteCustomAttirbutes) => {
  return Object.keys(allLocalCustomAttributes).reduce((acc, name) => {
    return {
      ...acc,
      [name]: mergeCustomAttributeType(
        allLocalCustomAttributes[name] ?? [],
        allRemoteCustomAttirbutes[name] ?? []
      ),
    }
  }, {})
}

/**
 * @typedef AllMergedAttributes
 * @property {MergedAttributes} characters
 */

/**
 * @typedef AllMergedCategories
 * @property {MergedCategories} characters
 * @property {MergedCategories} places
 * @property {MergedCategories} notes
 * @property {MergedCategories} tags
 */

/**
 * @typedef MergedCategories
 * @property {Record<number, number>} conflictedCategoryMapping
 * @property {Record<number, Category>} mergedCategories
 */

/**
 * @param {Array<Category>} onlineCategories
 * @param {Array<Category>} offlineCategories
 * @return {MergedCategories}
 */
export const mergeCategoryType = (onlineCategories, offlineCategories) => {
  let maxId = onlineCategories.reduce((acc, { id }) => {
    if (typeof id === 'number') {
      return Math.max(acc, id)
    } else {
      return id
    }
  }, 0)
  let maxPosition = onlineCategories.reduce((acc, { position }) => {
    if (typeof position === 'number') {
      return Math.max(acc, position)
    } else {
      return position
    }
  }, 0)
  // Offline entities might have their category ids shifted by the
  // merge.
  /**
   * @type {Record<number, number>}
   */
  const conflictedCategoryMapping = {}
  const mergedCategories = Object.values(
    offlineCategories.reduce(
      (acc, next) => {
        const conflict = () => {
          maxId++
          conflictedCategoryMapping[next.id] = maxId
          return {
            ...acc,
            [maxId]: {
              ...next,
              id: maxId,
            },
          }
        }
        /**
         * @param {Record<number, Category>} categories
         * @param {number} id
         * @return {Record<number, Category>}
         */
        const bumpPosition = (categories, id) => {
          maxPosition++
          return {
            ...categories,
            [id]: {
              ...categories[id],
              position: maxPosition,
            },
          }
        }
        const existingCategory = acc[next.id]
        if (typeof existingCategory === 'object') {
          if (existingCategory.name === next.name) {
            return acc
          } else if (next.position <= maxPosition) {
            // Beware the side effect that `conflict` has on `maxId`!!
            const withConflictResolved = conflict()
            return bumpPosition(withConflictResolved, maxId)
          } else {
            return conflict()
          }
        } else if (next.position <= maxPosition) {
          return bumpPosition(
            {
              ...acc,
              [next.id]: next,
            },
            next.id
          )
        } else {
          return {
            ...acc,
            [next.id]: next,
          }
        }
      },
      keyBy(onlineCategories, 'id')
    )
  )
  return {
    conflictedCategoryMapping,
    mergedCategories,
  }
}

/**
 * @param {FileCategories} allOnlineCategories
 * @param {FileCategories} allOfflineCategories
 * @return {AllMergedCategories}
 */
export const mergeCategories = (allOnlineCategories, allOfflineCategories) => {
  /**
   * @param {AllMergedCategories} acc
   * @param {keyof FileCategories} name
   * @return {AllMergedCategories}
   */
  const accumulateMergedCategories = (acc, name) => {
    return {
      ...acc,
      [name]: mergeCategoryType(allOnlineCategories[name], allOfflineCategories[name]),
    }
  }

  return Object.keys(allOnlineCategories).reduce(accumulateMergedCategories, {
    characters: {
      conflictedCategoryMapping: {},
      mergedCategories: {},
    },
    places: {
      conflictedCategoryMapping: {},
      mergedCategories: {},
    },
    notes: {
      conflictedCategoryMapping: {},
      mergedCategories: {},
    },
    tags: {
      conflictedCategoryMapping: {},
      mergedCategories: {},
    },
  })
}

/**
 * @param {Series} onlineSeries
 * @param {Series} offlineSeries
 * @return {{ onlineSeries: Series, offlineSeries: Series } | Null}
 */
const seriesDiff = (onlineSeries, offlineSeries) => {
  return !isEqualTreatingUndefinedOrMissingAsTheSame(onlineSeries, offlineSeries)
    ? { onlineSeries, offlineSeries }
    : null
}

/**
 * @param {Record<String, FamilyTree>} onlineFamilyTrees
 * @param {Record<String, FamilyTree>} offlineFamilyTrees
 * @return {EntityDiffs<FamilyTree>}
 */
const familyTreeDiff = (onlineFamilyTrees, offlineFamilyTrees) => {
  const allIds = union(Object.keys(onlineFamilyTrees), Object.keys(offlineFamilyTrees))
  return allIds.reduce(
    (acc, id) => {
      if (ATTRIBUTES_TO_STRIP_DURING_DIFF.includes(id)) {
        return acc
      } else {
        const onlineFamilyTree = onlineFamilyTrees[id]
        const offlineFamilyTree = offlineFamilyTrees[id]
        if (!onlineFamilyTree) {
          if (offlineFamilyTree) {
            return [[...acc[0], offlineFamilyTree], acc[1]]
          } else {
            return acc
          }
        } else if (
          onlineFamilyTree &&
          offlineFamilyTree &&
          !isEqual(onlineFamilyTree, offlineFamilyTree)
        ) {
          return [
            acc[0],
            [...acc[1], { onlineEntity: onlineFamilyTree, offlineEntity: offlineFamilyTree }],
          ]
        } else {
          return acc
        }
      }
    },
    [[], []]
  )
}

const ARRAY_KEYS = ['cards', 'characters', 'lines', 'notes', 'places', 'tags']

/**
 * @template A
 * @param {A} entity
 * @return {Partial<A>}
 */
const omitNonDiffableData = (entity) => {
  if (entity && typeof entity === 'object') {
    return omit(entity, ATTRIBUTES_TO_STRIP_DURING_DIFF)
  } else {
    return entity
  }
}

/**
 * @param {PlottrFile} offlineFile
 * @param {PlottrFile} onlineFile
 * @return {Array<String>}
 */
export const diffPerKey = (offlineFile, onlineFile) => {
  /**
   * @param {keyof PlottrFile} key
   * @return {String | Null}
   */
  const diffForFileAtKey = (key) => {
    if (ARRAY_KEYS.includes(key)) {
      if (
        Array.isArray(offlineFile[key]) &&
        Array.isArray(onlineFile[key]) &&
        !isEqualTreatingUndefinedOrMissingAsTheSame(
          sortBy(offlineFile[key].map(omitNonDiffableData), 'id'),
          sortBy(onlineFile[key].map(omitNonDiffableData), 'id').slice(0, offlineFile[key].length)
        )
      ) {
        return key
      } else {
        return null
      }
    } else if (!isEqualTreatingUndefinedOrMissingAsTheSame(offlineFile[key], onlineFile[key])) {
      return key
    } else {
      return null
    }
  }

  return Object.keys(offlineFile)
    .map(diffForFileAtKey)
    .filter((x) => {
      return typeof x === 'string'
    })
}

/**
 * @param {PlottrFile} onlineFile
 * @param {ConflictResolutionLocalOnly} localOnly
 * @param {ConflictResolutionChangedLocalAndOnline} changedLocalAndOnline
 * @param {Record<String, Array<LegacyCustomAttribute>>} customAttributes
 * @param {SeriesConflict | Null} seriesDiffResult
 * @return {{ type: 'SET_CONFLICT_RESOLUTION_DATA', onlineFile: PlottrFile, localOnly: ConflictResolutionLocalOnly, changedLocalAndOnline: ConflictResolutionChangedLocalAndOnline, customAttributes: Record<String, Array<LegacyCustomAttribute>>, seriesDiff: SeriesConflict | Null }}
 */
export const _setConflictResolutionData = (
  onlineFile,
  localOnly,
  changedLocalAndOnline,
  customAttributes,
  seriesDiffResult
) => {
  return {
    type: SET_CONFLICT_RESOLUTION_DATA,
    onlineFile,
    localOnly,
    changedLocalAndOnline,
    customAttributes,
    seriesDiff: seriesDiffResult,
  }
}

/**
 * @return {{ type: 'CLOSE_CONFLICT_RESOLUTION_MODAL' }}
 */
export const _closeConflictResolutionModal = () => {
  return {
    type: CLOSE_CONFLICT_RESOLUTION_MODAL,
  }
}

/**
 * @param {PlottrFile} onlineFile
 * @param {function(String, String, PlottrFile, FilePermission): Promise<Void>} overwriteAllKeys
 * @param {function(function(): Promise<Void>, Logger): Promise<Void>} retryWithBackOff
 * @param {Logger} logger
 * @return {function(Dispatch, GetState): Void}
 */
export const computeAndSetResumeData =
  (onlineFile, overwriteAllKeys, retryWithBackOff, logger) => (dispatch, getState) => {
    const onlineState = { user: onlineFile }
    const state = getState()
    const keysThatDiffer = diffPerKey(fullFileStateSelector(state), onlineFile)

    const onlyUIOrFileDiffer = keysThatDiffer.every((key) => {
      return ['ui', 'file'].includes(key)
    })
    if (onlyUIOrFileDiffer) {
      // Don't try to resolve differences in the ui or file keys.
      // Instead, load up the online versions.
      dispatch(patchFile(false, onlineFile.file))
      dispatch(loadUI(false, onlineFile.ui))
      dispatch(setResuming(false))
    } else {
      const mergedCustomAttributes = mergeCustomAttributes(
        allCustomAttributesSelector(state),
        allCustomAttributesSelector(onlineState)
      )
      const [localOnlyNotes, localAndOnlineNotes] = arrayEntityDiff(
        allNotesSelector(state),
        allNotesSelector(onlineState)
      )
      const [localOnlyCharacters, localAndOnlineCharacters] = arrayEntityDiff(
        allCharactersSelector(state),
        allCharactersSelector(onlineState)
      )
      const [localOnlyPlaces, localAndOnlinePlaces] = arrayEntityDiff(
        allPlacesSelector(state),
        allPlacesSelector(onlineState)
      )
      const [localOnlyTags, localAndOnlineTags] = arrayEntityDiff(
        allTagsSelector(state),
        allTagsSelector(onlineState)
      )
      const [localOnlyBooks, localAndOnlineBooks] = bookDiff(
        allBooksWithAllAssociatedDataSelector(state),
        allBooksWithAllAssociatedDataSelector(onlineState),
        onlineState
      )
      const onlineSeries = seriesSelector(onlineState)
      const offlineSeries = seriesSelector(state)
      const seriesEntitiesOrNull =
        onlineSeries && offlineSeries ? seriesDiff(onlineSeries, offlineSeries) : null
      const [localOnlyFamilyTrees, localAndOnlineFamilyTrees] = familyTreeDiff(
        allFamilyTreesSelector(onlineState),
        allFamilyTreesSelector(state)
      )

      /**
       * @type {ConflictResolutionLocalOnly}
       */
      const localOnly = {
        notes: localOnlyNotes.map(defaultLocalAction),
        characters: localOnlyCharacters.map(defaultLocalAction),
        places: localOnlyPlaces.map(defaultLocalAction),
        tags: localOnlyTags.map(defaultLocalAction),
        books: localOnlyBooks.map(defaultLocalAction),
        familyTrees: localOnlyFamilyTrees.map(defaultLocalAction),
      }

      /**
       * @typep {ConflictResolutionChangedLocalAndOnline}
       */
      const changedLocalAndOnline = {
        notes: localAndOnlineNotes.map(defaultLocalAndOnlineAction),
        characters: localAndOnlineCharacters.map(defaultLocalAndOnlineAction),
        places: localAndOnlinePlaces.map(defaultLocalAndOnlineAction),
        tags: localAndOnlineTags.map(defaultLocalAndOnlineAction),
        books: localAndOnlineBooks.map(defaultLocalAndOnlineAction),
        familyTrees: localAndOnlineFamilyTrees.map(defaultLocalAndOnlineAction),
      }

      /**
       * @type {SeriesConflict | Null}
       */
      const seriesDiffResult = seriesEntitiesOrNull
        ? {
            action: 'OVERWRITE',
            data: seriesEntitiesOrNull,
          }
        : null

      dispatch(
        _setConflictResolutionData(
          onlineFile,
          localOnly,
          changedLocalAndOnline,
          mergedCustomAttributes,
          seriesDiffResult
        )
      )
      const onlyAttributesCategoriesAndCustomAttributesDiffer =
        !onlyUIOrFileDiffer &&
        keysThatDiffer.every((key) => {
          return ['ui', 'file', 'customAttributes', 'attributes', 'categories'].includes(key)
        })
      // If there are differences that the user has no say in resolving.
      // Simply resolve them.
      if (onlyAttributesCategoriesAndCustomAttributesDiffer) {
        dispatch(actionConflictChanges())
        dispatch(setOverwritingCloudWithBackup(true))
        const fileId = fileIdSelector(getState())
        const clientId = clientIdSelector(getState())
        const withoutSystemKeys = fullFileStateSelector(getState())
        const permission = permissionSelector(getState())
        if (fileId && clientId && permission) {
          retryWithBackOff(() => {
            return overwriteAllKeys(fileId, clientId, withoutSystemKeys, permission)
          }, logger).then(() => {
            dispatch(setOverwritingCloudWithBackup(false))
            dispatch(setResuming(false))
            dispatch(_closeConflictResolutionModal())
          })
        } else {
          logger.error(
            new Error('System in bad state for resuming'),
            'No file ID or no client ID or no permission'
          )
        }
      }
    }
  }

/**
 * @param {'localOnly' | 'changedLocalAndOnline'} location
 * @param {'familyTrees' | 'notes' | 'characters' | 'places' | 'tags' | 'books'} type
 * @param {number} id
 * @param {'DUPLICATE' | 'DISCARD' | 'OVERWRITE'} action
 * @return {{ type: SET_CONFLICT_RESOLUTION_ITEM_ACTION, location: 'metadata' | 'localOnly' | 'changedLocalAndOnline', entityType: 'seriesDiff' | 'familyTrees' | 'notes' | 'characters' | 'places' | 'tags' | 'books', id: number, action: 'DUPLICATE' | 'DISCARD' | 'OVERWRITE' }}
 */
export const setConflictResolutionAction = (location, type, id, action) => {
  return {
    type: SET_CONFLICT_RESOLUTION_ITEM_ACTION,
    location,
    entityType: type,
    id,
    action,
  }
}

/**
 * @param {any} id
 * @return {function({ data: { id: any } }): boolean}
 */
const dataIdEquals = (id) => (entity) => {
  return entity.data.id === id
}

/**
 * @param {MissingEntities} rawAlreadyMissing
 * @param {'books' | 'tags' | 'familyTrees' | 'characters' | 'notes' | 'places'} type
 * @param {Note | Character | Place | Tag | BookWithAllAssociatedData | FamilyTree} entity
 * @param {PlottrState} state
 * @param {ConflictChanges} localOnly
 * @return {MissingEntities}
 */
export const withMissingEntitiesOfType = (rawAlreadyMissing, type, entity, state, localOnly) => {
  const alreadyMissing = {
    customAttributes: Object.assign(
      {},
      {
        tags: [],
        characters: [],
        places: [],
        notes: [],
        books: [],
      },
      rawAlreadyMissing.customAttributes
    ),
    attributes: Object.assign({}, rawAlreadyMissing.attributes),
  }
  const tagsAttributeId =
    characterAttributesForBookSelector(state).find(({ type, name }) => {
      return type === 'base-attribute' && name === 'tags'
    })?.id ?? null
  /**
   * @param {number} tagId
   */
  const tagIdIsMissing = (tagId) => {
    return !singleTagSelector(state, tagId) && !localOnly.tags.some(dataIdEquals(tagId))
  }
  /**
   * @return {Array<number>}
   */
  const missingTags = () => {
    if (type !== 'characters') {
      return 'tags' in entity && Array.isArray(entity.tags)
        ? entity.tags.filter(tagIdIsMissing)
        : []
    } else {
      return []
    }
  }
  /**
   * @return {{ id: number | null, values: Array<number> } | Null}
   */
  const missingTagsForCharacter = () => {
    if (type === 'characters' && typeof tagsAttributeId === 'number') {
      const tagsAttribute = ('attributes' in entity ? entity.attributes : []).find(({ id }) => {
        return id === tagsAttributeId
      })
      return {
        id: tagsAttributeId,
        values:
          tagsAttribute &&
          Array.isArray(tagsAttribute.value) &&
          tagsAttribute.value.every((x) => typeof x === 'number')
            ? tagsAttribute.value.filter(tagIdIsMissing)
            : [],
      }
    } else {
      return null
    }
  }
  const missingCharacters = () => {
    return 'characters' in entity && Array.isArray(entity.characters)
      ? entity.characters.filter((characterId) => {
          return (
            !singleCharacterSelector(state, characterId) &&
            !localOnly.characters.find(dataIdEquals(characterId))
          )
        })
      : []
  }
  const missingPlaces = () => {
    return 'places' in entity && Array.isArray(entity.places)
      ? entity.places.filter((placeId) => {
          return (
            !singlePlaceSelector(state, placeId) && !localOnly.places.find(dataIdEquals(placeId))
          )
        })
      : []
  }
  /**
   * @return {Array<'series' | number>}
   */
  const missingBooks = () => {
    /**
     * @param {Array<'series' | number>} acc
     * @param {'series' | number} bookId
     * @return {Array<'series' | number>}
     */
    const accumulator = (acc, bookId) => {
      if (
        (typeof bookId === 'number' || bookId === 'series') &&
        !singleBookSelector(state, bookId) &&
        !localOnly.books.find((entry) => entry.data.book.id === bookId)
      ) {
        return [...acc, bookId]
      } else {
        return acc
      }
    }
    const bookIds = 'bookIds' in entity ? entity.bookIds : []
    return Array.isArray(bookIds) ? bookIds.reduce(accumulator, []) : []
  }
  switch (type) {
    case 'notes': {
      return {
        ...alreadyMissing,
        customAttributes: {
          ...alreadyMissing.customAttributes,
          books: uniq([...missingBooks(), ...alreadyMissing.customAttributes.books]),
          tags: uniq([...missingTags(), ...alreadyMissing.customAttributes.tags]),
          characters: uniq([...missingCharacters(), ...alreadyMissing.customAttributes.characters]),
          places: uniq([...missingPlaces(), ...alreadyMissing.customAttributes.places]),
        },
      }
    }
    case 'characters': {
      const missingTagsObject = missingTagsForCharacter()
      return {
        ...alreadyMissing,
        customAttributes: {
          ...alreadyMissing.customAttributes,
          books: uniq([...missingBooks(), ...(alreadyMissing?.attributes?.books ?? [])]),
        },
        attributes: {
          ...alreadyMissing.attributes,
          ...(tagsAttributeId
            ? {
                tags:
                  Array.isArray(missingTagsObject?.values) && missingTagsObject?.values?.length > 0
                    ? {
                        values: uniq([
                          ...(missingTagsObject?.values ?? []),
                          ...(alreadyMissing.attributes?.tags?.values ?? []),
                        ]),
                        id: tagsAttributeId,
                      }
                    : alreadyMissing.attributes.tags,
              }
            : {}),
        },
      }
    }
    case 'places': {
      return {
        ...alreadyMissing,
        customAttributes: {
          ...alreadyMissing.customAttributes,
          books: uniq([...missingBooks(), ...alreadyMissing.customAttributes.books]),
          tags: uniq([...missingTags(), ...alreadyMissing.customAttributes.tags]),
        },
      }
    }
    default: {
      return alreadyMissing
    }
  }
}

/**
 * @typedef MissingEntities
 * @property {MissingCustomAttributes} customAttributes
 * @property {MissingAttributes} attributes
 */

/**
 * @typedef MissingCustomAttributes
 * @property {Array<number>} characters
 * @property {Array<number>} places
 * @property {Array<number>} notes
 * @property {Array<number>} tags
 * @property {Array<'series' | number>} books
 */

/**
 * @typedef MissingAttributes
 * @property {{ id: number, values: Array<number> } | Null} tags
 * @property {Array<'series' | number>} books
 */

/**
 * @param {PlottrState} state
 * @param {ConflictChanges} localOnly
 * @param {LocalAndOnlineConflictChanges} changedLocalAndOnline
 * @return {MissingEntities}
 */
export const findMissingEntities = (state, localOnly, changedLocalAndOnline) => {
  const withMissingFromLocal = Object.entries(localOnly).reduce(
    (acc, next) => {
      const [type, entries] = next
      return entries.reduce((accWithEntries, { action, data }) => {
        if (
          action === 'INCLUDE' &&
          (type === 'notes' ||
            type === 'characters' ||
            type === 'places' ||
            type === 'tags' ||
            type === 'books' ||
            type === 'familyTrees')
        ) {
          return withMissingEntitiesOfType(accWithEntries, type, data, state, localOnly)
        } else {
          return accWithEntries
        }
      }, acc)
    },
    {
      customAttributes: {
        tags: [],
        characters: [],
        places: [],
        notes: [],
        books: [],
      },
      attributes: {
        tags: null,
        books: [],
      },
    }
  )
  const withMissingFromConflicts = Object.entries(changedLocalAndOnline).reduce((acc, next) => {
    const [type, entries] = next
    return entries.reduce((accWithEntries, { data }) => {
      const offlineEntity = data.offlineEntity
      if (
        offlineEntity &&
        (type === 'notes' ||
          type === 'characters' ||
          type === 'places' ||
          type === 'tags' ||
          type === 'books' ||
          type === 'familyTrees')
      ) {
        return withMissingEntitiesOfType(accWithEntries, type, offlineEntity, state, localOnly)
      } else {
        return accWithEntries
      }
    }, acc)
  }, withMissingFromLocal)
  return withMissingFromConflicts
}

/**
 * @typedef ActionableOnlineAndOfflineBookChanges
 * @property {Array<ActionableData<LocalAndOnline<BookWithAllAssociatedData>>>} changedLocalAndOnlineWithCorrectedBookIds
 * @property {Array<ActionableData<BookWithAllAssociatedData>>} localOnlyWithCorrectedBookIds
 */

/**
 * @param {PlottrState} onlineFile
 * @param {Array<ActionableData<BookWithAllAssociatedData>>} localOnlyBooks
 * @param {Array<ActionableData<LocalAndOnline<BookWithAllAssociatedData>>>} changedLocalAndOnlineBooks
 * @return {ActionableOnlineAndOfflineBookChanges}
 */
export const bumpBookIds = (onlineFile, localOnlyBooks, changedLocalAndOnlineBooks) => {
  let currentBookId = nextId(allBooksAsArraySelector(onlineFile))
  const localOnlyWithCorrectedBookIds = localOnlyBooks.reduce((acc, next) => {
    const {
      data: { book, lines, cards, beats, hierarchy },
      action,
    } = next
    if (action === 'INCLUDE') {
      const newBookId = currentBookId
      currentBookId++
      return [
        ...acc,
        {
          data: {
            book: {
              ...book,
              id: newBookId,
            },
            lines: lines.map((line) => {
              return {
                ...line,
                bookId: newBookId,
              }
            }),
            cards,
            beats: {
              ...beats,
              index: beats.index
                ? Object.values(beats.index).reduce((newIndex, nextBeat) => {
                    return {
                      [nextBeat.id]: {
                        ...nextBeat,
                        bookId: newBookId,
                      },
                      ...newIndex,
                    }
                  }, {})
                : beats.index,
            },
            hierarchy,
          },
          action,
        },
      ]
    } else {
      return [...acc, next]
    }
  }, [])

  const changedLocalAndOnlineWithCorrectedBookIds = changedLocalAndOnlineBooks.reduce(
    (acc, next) => {
      const {
        data: { onlineEntity, offlineEntity },
        action,
      } = next
      if (action === 'DUPLICATE' && offlineEntity) {
        const { book, lines, cards, beats, hierarchy } = offlineEntity
        const newBookId = currentBookId
        currentBookId++
        return [
          ...acc,
          {
            data: {
              onlineEntity,
              offlineEntity: {
                book: {
                  ...book,
                  id: newBookId,
                },
                lines: lines.map((line) => {
                  return {
                    ...line,
                    bookId: newBookId,
                  }
                }),
                cards,
                beats: {
                  ...beats,
                  index: beats.index
                    ? Object.values(beats.index).reduce((newIndex, nextBeat) => {
                        return {
                          [nextBeat.id]: {
                            ...nextBeat,
                            bookId: newBookId,
                          },
                          ...newIndex,
                        }
                      }, {})
                    : {},
                },
                hierarchy,
              },
            },
            action,
          },
        ]
      } else {
        return [...acc, next]
      }
    },
    []
  )

  return {
    changedLocalAndOnlineWithCorrectedBookIds,
    localOnlyWithCorrectedBookIds,
  }
}

/**
 * @param {PlottrState} localFile
 * @param {PlottrState} remoteFile
 * @return {{ localIdMap: Record<number, number>, allImages: Record<String, ImageRecord> }}
 */
export const mergeImages = (localFile, remoteFile) => {
  const localImages = imagesSelector(localFile)
  const remoteImages = imagesSelector(remoteFile)

  const { localIdMap, allImages } = Object.values(localImages).reduce(
    (acc, next) => {
      const existingImage = acc.allImages[next.id]
      if (
        typeof existingImage === 'object' &&
        !isEqualTreatingUndefinedOrMissingAsTheSame(omit(existingImage, 'id'), omit(next, 'id'))
      ) {
        const nextImageId =
          Object.values(acc.allImages)
            .map(({ id }) => {
              return id
            })
            .reduce((maxImageId, indexedImageId) => {
              if (typeof indexedImageId === 'number') {
                return Math.max(maxImageId, indexedImageId)
              } else {
                return maxImageId
              }
            }, 0) + 1
        return {
          localIdMap: {
            ...acc.localIdMap,
            [next.id]: nextImageId,
          },
          allImages: {
            ...acc.allImages,
            [nextImageId]: {
              ...next,
              id: nextImageId,
            },
          },
        }
      } else {
        return {
          localIdMap: {
            ...acc.localIdMap,
            [next.id]: next.id,
          },
          allImages: {
            ...acc.allImages,
            [next.id]: next,
          },
        }
      }
    },
    {
      localIdMap: {},
      allImages: remoteImages,
    }
  )

  return {
    localIdMap,
    allImages,
  }
}

/**
 * @typedef Identifiable
 * @property {number} id
 */

/**
 * @param {Array<Identifiable>} existingEntities
 * @param {Array<ActionableData<Identifiable>>} localEntities
 * @param {Array<ActionableData<LocalAndOnline<Identifiable>>>} localAndRemoteEntities
 * @return {Record<String, number>}
 */
const translatedIds = (existingEntities, localEntities, localAndRemoteEntities) => {
  let currentId =
    existingEntities.reduce((acc, next) => {
      if (typeof next.id === 'number') {
        return Math.max(acc, next.id)
      } else {
        return acc
      }
    }, 0) + 1

  const existingIdMapping = existingEntities.reduce((acc, next) => {
    return {
      ...acc,
      [next.id]: next.id,
    }
  }, {})

  const localEntityMapping = localEntities.reduce((acc, next) => {
    if (next.action === 'INCLUDE') {
      return {
        ...acc,
        [next.data.id]: currentId++,
      }
    } else {
      return acc
    }
  }, existingIdMapping)

  return localAndRemoteEntities.reduce((acc, next) => {
    if (next.action === 'DUPLICATE') {
      return {
        ...acc,
        [next.data.onlineEntity.id]: currentId++,
      }
    } else {
      return acc
    }
  }, localEntityMapping)
}

/**
 * This function should be called on the result of bumping the book
 * ids because it produces an identity mapping for the related ids.
 * i.e. it trusts that bumping the book ids was already successful.
 *
 * @param {Array<Book>} existingEntities
 * @param {Array<ActionableData<BookWithAllAssociatedData>>} localOnlyBooks
 * @param {Array<ActionableData<LocalAndOnline<BookWithAllAssociatedData>>>} localAndRemoteBooks
 * @return {{ books: Record<number, number>, hierarchy: Record<number, number>, beats: Record<number, number>, cards: Record<number, number>, lines: Record<number, number> }}
 */
const translatedBookIds = (existingEntities, localOnlyBooks, localAndRemoteBooks) => {
  /**
   * @param {Record<number, number>} acc
   * @param {HasId} _next
   * @return {Record<number, number>}
   */
  const accIds = (acc, { id }) => {
    return {
      ...acc,
      [id]: id,
    }
  }

  /**
   * @type {Array<keyof BookWithAllAssociatedData>}
   */
  const bundleEntitiesToExtractIds = ['cards', 'lines']

  /**
   * @typedef {{ books: {}, hierarchy: {}, beats: {}, cards: {}, lines: {} }} IdMapping
   */

  /**
   * @type {IdMapping}
   */
  const existingIdMapping = existingEntities.reduce(
    (acc, next) => {
      const bookId = next.id
      return {
        ...acc,
        books: { ...acc.books, [bookId]: bookId },
        hierarchy: { ...acc.hierarchy, [bookId]: bookId },
        beats: { ...acc.beats, [bookId]: bookId },
      }
    },
    { books: {}, hierarchy: {}, beats: {}, cards: {}, lines: {} }
  )

  const localIdMapping = localOnlyBooks.reduce((acc, nextBundle) => {
    const bookId = nextBundle.data.book.id
    if (bookId) {
      /**
       * @param {Record<keyof IdMapping, Record<number, number>>} bundleAcc
       * @param {keyof BookWithAllAssociatedData} bundleKey
       * @return {Record<keyof IdMapping | 'books', Record<number, number>>}
       */
      const accumulateBundleEntitiesToExtractIds = (bundleAcc, bundleKey) => {
        const bundleValue = nextBundle.data[bundleKey]
        return {
          ...bundleAcc,
          [bundleKey]: {
            ...(bundleAcc[bundleKey === 'book' ? 'books' : bundleKey] ?? {}),
            ...Object.values(bundleValue).reduce(accIds, {}),
          },
        }
      }
      return bundleEntitiesToExtractIds.reduce(accumulateBundleEntitiesToExtractIds, {
        ...acc,
        books: { ...(acc.books ?? {}), [bookId]: bookId },
        hierarchy: { ...(acc.hierarchy ?? {}), [bookId]: bookId },
        beats: { ...(acc.beats ?? {}), [bookId]: bookId },
      })
    } else {
      return acc
    }
  }, existingIdMapping)

  return localAndRemoteBooks.reduce((acc, nextBundle) => {
    const offlineEntity = nextBundle.data.offlineEntity
    const bookId = nextBundle.data.offlineEntity?.book?.id
    if (offlineEntity && bookId) {
      return bundleEntitiesToExtractIds.reduce(
        (bundleAcc, bundleKey) => {
          const bundleValue = offlineEntity[bundleKey]
          return {
            ...bundleAcc,
            [bundleKey]: {
              ...(bundleAcc[bundleKey === 'book' ? 'books' : bundleKey] ?? {}),
              ...Object.values(bundleValue).reduce(accIds, {}),
            },
          }
        },
        {
          ...acc,
          books: { ...(acc.books ?? {}), [bookId]: bookId },
          hierarchy: { ...(acc.hierarchy ?? {}), [bookId]: bookId },
          beats: { ...(acc.beats ?? {}), [bookId]: bookId },
        }
      )
    } else {
      return acc
    }
  }, localIdMapping)
}

/**
 * @param {ConflictResolutionLocalOnly} localOnly
 * @param {ConflictResolutionChangedLocalAndOnline} changedLocalAndOnline
 * @param {SeriesConflict | Null} seriesConflict
 * @param {MissingEntities} missingEntities
 * @param {PlottrFile} onlineFile
 * @param {AllMergedAttributes} mergedAttributes
 * @param {Record<String, Array<LegacyCustomAttribute>>} customAttributes
 * @param {AllMergedCategories} mergedCategories
 * @param {{ localIdMap: Record<number, number>, allImages: Record<String, ImageRecord> }} mergedImages
 * @param {IdMapping} idMapping
 * @return {{ type: 'ACTION_CONFLICT_CHANGES', localOnly: ConflictResolutionLocalOnly, changedLocalAndOnline: ConflictResolutionChangedLocalAndOnline, seriesConflict: SeriesConflict | Null, missingEntities: MissingEntities, onlineFile: PlottrFile, mergedAttributes: AllMergedAttributes, customAttributes: Record<String, Array<LegacyCustomAttribute>>, mergedCategories: AllMergedCategories, mergedImages: { localIdMap: Record<number, number>, allImages: Record<String, ImageRecord> }, idMapping: IdMapping }}
 */
export const _actionConflictChanges = (
  localOnly,
  changedLocalAndOnline,
  seriesConflict,
  missingEntities,
  onlineFile,
  mergedAttributes,
  customAttributes,
  mergedCategories,
  mergedImages,
  idMapping
) => {
  return {
    type: ACTION_CONFLICT_CHANGES,
    localOnly,
    changedLocalAndOnline,
    seriesConflict,
    missingEntities,
    onlineFile,
    mergedAttributes,
    customAttributes,
    mergedCategories,
    mergedImages,
    idMapping,
  }
}

/**
 * @typedef IdMapping
 * @property {Record<String, number>} notes
 * @property {Record<String, number>} characters
 * @property {Record<String, number>} places
 * @property {Record<String, number>} tags
 * @property {Record<String, number>} cards
 * @property {Record<String, number>} lines
 * @property {Record<String, number>} beats
 * @property {Record<String, number>} books
 * @property {Record<String, number>} hierarchy
 */

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const actionConflictChanges = () => (dispatch, getState) => {
  const state = getState()
  const onlineFile = onlineFileForConflictResolutionSelector(state)
  const localOnly = localOnlyConflictChangesSelector(state)
  const changedLocalAndOnline = localAndOnlineConflictChangesSelector(state)
  const customAttributes = conflictResolutionCustomAttributesSelector(state)
  if (onlineFile && localOnly && changedLocalAndOnline && customAttributes) {
    const seriesConflict = seriesConflictSelector(state)
    const onlineState = { user: onlineFile }
    const mergedAttributes = mergeAttributes(
      attributesSelector(onlineState),
      attributesSelector(state)
    )
    const mergedCategories = mergeCategories(
      allCategoriesSelector(onlineState),
      allCategoriesSelector(state)
    )
    const mergedImages = mergeImages(state, onlineState)
    const imagesWereAdded = !isEqualTreatingUndefinedOrMissingAsTheSame(
      mergedImages.allImages,
      imagesSelector(getState())
    )
    const { localOnlyWithCorrectedBookIds, changedLocalAndOnlineWithCorrectedBookIds } =
      bumpBookIds(onlineState, localOnly.books, changedLocalAndOnline.books)
    const onlineCustomAttributes = allCustomAttributesSelector(onlineState)
    const onlineAttributes = attributesSelector(onlineState)
    const onlineCategories = allCategoriesSelector(onlineState)
    if (
      typeof onlineFile === 'object' &&
      onlineFile &&
      (!isEmpty(localOnly.notes) ||
        !isEmpty(localOnly.characters) ||
        !isEmpty(localOnly.familyTrees) ||
        !isEmpty(localOnly.places) ||
        !isEmpty(localOnly.tags) ||
        !isEmpty(localOnly.books) ||
        !isEmpty(changedLocalAndOnline.notes) ||
        !isEmpty(changedLocalAndOnline.characters) ||
        !isEmpty(changedLocalAndOnline.familyTrees) ||
        !isEmpty(changedLocalAndOnline.places) ||
        !isEmpty(changedLocalAndOnline.tags) ||
        !isEmpty(changedLocalAndOnline.books) ||
        !isEqual(customAttributes, onlineCustomAttributes) ||
        !isEqual(mergedAttributes, onlineAttributes) ||
        !isEqual(mergedCategories, onlineCategories) ||
        imagesWereAdded)
    ) {
      const missingEntities = findMissingEntities(state, localOnly, changedLocalAndOnline)
      const currentNotes = allNotesSelector(onlineState)
      const noteIdMapping = translatedIds(
        currentNotes,
        localOnly.notes,
        changedLocalAndOnline.notes
      )
      const currentCharacters = allCharactersSelector(onlineState)
      const characterIdMapping = translatedIds(
        currentCharacters,
        localOnly.characters,
        changedLocalAndOnline.characters
      )
      const currentPlaces = allPlacesSelector(onlineState)
      const placeIdMapping = translatedIds(
        currentPlaces,
        localOnly.places,
        changedLocalAndOnline.places
      )
      const currentTags = allTagsSelector(onlineState)
      const tagIdMapping = translatedIds(currentTags, localOnly.tags, changedLocalAndOnline.tags)
      const currentBooks = allBooksAsArraySelector(onlineState)
      const bookIdMappings = translatedBookIds(
        currentBooks,
        localOnlyWithCorrectedBookIds,
        changedLocalAndOnlineWithCorrectedBookIds
      )

      /**
       * @type {IdMapping}
       */
      const idMapping = {
        notes: noteIdMapping,
        characters: characterIdMapping,
        places: placeIdMapping,
        tags: tagIdMapping,
        // TODO: check whether we really don't compute card and line
        // mappings yet.  We might not because we bump the entire
        // books ids as one.
        cards: bookIdMappings.cards ?? {},
        lines: bookIdMappings.lines ?? {},
        beats: bookIdMappings.beats ?? {},
        books: bookIdMappings.books ?? {},
        hierarchy: bookIdMappings.hierarchy ?? {},
      }

      dispatch(
        _actionConflictChanges(
          {
            ...localOnly,
            books: localOnlyWithCorrectedBookIds,
          },
          {
            ...changedLocalAndOnline,
            books: changedLocalAndOnlineWithCorrectedBookIds,
          },
          seriesConflict,
          missingEntities,
          onlineFile,
          mergedAttributes,
          customAttributes,
          mergedCategories,
          mergedImages,
          idMapping
        )
      )
    }
  } else {
    // TODO(ed): handle the case that we tried to action conflict
    // changes and there wasn't a local or there wasn't an online
    // version of the file stored/diffed.
  }
}

/**
 * @return {{ type: 'BOOTED_OFFLINE_FILE_WHILST_ONLINE' }}
 */
export const bootedOfflineFileWhilstOnline = () => {
  return {
    type: BOOTED_OFFLINE_FILE_WHILST_ONLINE,
  }
}
